/**
 * Core-paused ads / keywords / ad groups that still need a re-enable check.
 *
 * Easy Yes can pause on this window. People forget to turn the entity back
 * on when the season turns. This list is the reminder — not a verdict that
 * the pause was wrong.
 *
 * Stored per tenant in localStorage (same convenience class as Easy No).
 * Live reads prune anything that is ENABLED again.
 */
import type {
  AdMonitorResponse,
  StructureResponse,
} from '@/services/googleAdsManagementService';

export const PAUSE_WATCH_PREFIX = 'agileco.strategy-paused.';

export type PauseWatchKind = 'ad' | 'keyword' | 'ad_group';

export interface PauseWatchItem {
  id: string;
  kind: PauseWatchKind;
  title: string;
  pausedAt: string;
  lookId: string;
  snoozeUntil?: string;
}

const LOOK_ID: Record<PauseWatchKind, string> = {
  ad: 'ad-health',
  keyword: 'keywords',
  ad_group: 'ad-groups',
};

export function pauseWatchLookId(kind: PauseWatchKind): string {
  return LOOK_ID[kind];
}

export function readPauseWatch(tenantId: string): PauseWatchItem[] {
  if (!tenantId) return [];
  try {
    const parsed = JSON.parse(
      window.localStorage.getItem(PAUSE_WATCH_PREFIX + tenantId) || '[]',
    );
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isPauseWatchItem);
  } catch {
    return [];
  }
}

export function writePauseWatch(tenantId: string, items: PauseWatchItem[]): void {
  try {
    window.localStorage.setItem(
      PAUSE_WATCH_PREFIX + tenantId,
      JSON.stringify(items),
    );
  } catch {
    // Reminder is convenience only.
  }
}

export function upsertPauseWatch(
  items: PauseWatchItem[],
  next: PauseWatchItem,
): PauseWatchItem[] {
  const rest = items.filter((item) => item.id !== next.id);
  return [...rest, next];
}

export function removePauseWatch(
  items: PauseWatchItem[],
  id: string,
): PauseWatchItem[] {
  return items.filter((item) => item.id !== id);
}

export function snoozePauseWatch(
  items: PauseWatchItem[],
  id: string,
  days = 14,
): PauseWatchItem[] {
  const until = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
  return items.map((item) =>
    item.id === id ? { ...item, snoozeUntil: until } : item,
  );
}

/** Drop entities that are live again. Keep the rest until the operator acts. */
export function pruneReenabledWatch(
  items: PauseWatchItem[],
  ads?: AdMonitorResponse,
  structure?: StructureResponse,
): PauseWatchItem[] {
  return items.filter((item) => stillPaused(item, ads, structure));
}

export function visiblePauseWatch(
  items: PauseWatchItem[],
  now = Date.now(),
): PauseWatchItem[] {
  return items.filter((item) => {
    if (!item.snoozeUntil) return true;
    const until = Date.parse(item.snoozeUntil);
    return Number.isNaN(until) || until <= now;
  });
}

function stillPaused(
  item: PauseWatchItem,
  ads?: AdMonitorResponse,
  structure?: StructureResponse,
): boolean {
  if (item.kind === 'ad') {
    if (!ads?.read_ok) return true;
    const row = ads.ads.find(
      (ad) => (ad.resource_name || ad.ad_id) === item.id,
    );
    if (!row) return true;
    return row.status === 'PAUSED';
  }
  if (item.kind === 'keyword') {
    if (!structure?.read_ok) return true;
    const row = structure.keywords.find(
      (keyword) => (keyword.resource_name || keyword.criterion_id) === item.id,
    );
    if (!row) return true;
    return row.status === 'PAUSED';
  }
  if (!structure?.read_ok) return true;
  const row = structure.ad_groups.find(
    (group) => (group.resource_name || group.ad_group_id) === item.id,
  );
  if (!row) return true;
  return row.status === 'PAUSED';
}

function isPauseWatchItem(value: unknown): value is PauseWatchItem {
  if (!value || typeof value !== 'object') return false;
  const row = value as PauseWatchItem;
  return (
    typeof row.id === 'string' &&
    (row.kind === 'ad' || row.kind === 'keyword' || row.kind === 'ad_group') &&
    typeof row.title === 'string' &&
    typeof row.pausedAt === 'string' &&
    typeof row.lookId === 'string'
  );
}
